import { ExternalBookingAPIKey } from '@models/ExternalBookingAPIKey'
import { externalBookingAPIKeySchema } from '@models/schema'
import { createSelector, createSlice } from '@reduxjs/toolkit'
import {
  createExternalBookingAPIKey as createExternalBookingAPIKeyAPI,
  deleteExternalBookingAPIKey as deleteExternalBookingAPIKeyAPI,
  fetchExternalBookingAPIKeys,
  updateExternalBookingAPIKey as updateExternalBookingAPIKeyAPI,
} from '@services/ExternalBookingAPIKeyService'
import { AppDispatch, RootState } from '@store/store'
import {
  baseReducers,
  getIdOrModelId,
  SBAPI,
  SBSelectRaw,
  SBState,
} from '@supplement-bacon/react-toolkit/utils'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'

const initialState: SBState<ExternalBookingAPIKey> & {
  tempCreatedApiKey?: string
} = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
  tempCreatedApiKey: undefined,
}

const slice = createSlice({
  name: 'externalBookingAPIKey',
  initialState,
  reducers: {
    ...baseReducers,
    setTempCreatedApiKey: (state, action) => {
      state.tempCreatedApiKey = action.payload
    },
  },
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getExternalBookingAPIKeysSuccess,
  setQuery: setExternalBookingAPIKeyQuery,
  reset: resetExternalBookingAPIKeyState,
  resetQueryAndIds: resetExternalBookingAPIKeyQueryAndIds,
  setTempCreatedApiKey,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<
  RootState,
  { [key: number]: ExternalBookingAPIKey }
> = (state: RootState) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectExternalBookingAPIKeys = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectSelectedExternalBookingAPIKey = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectExternalBookingAPIKeyById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectExternalBookingAPIKeysByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )

/**
 * Actions
 */

export const setSelectedExternalBookingAPIKey =
  (externalBookingAPIKey: ExternalBookingAPIKey | number) =>
  async (dispatch: AppDispatch) =>
    dispatch(
      slice.actions.setSelectedId(
        getIdOrModelId<ExternalBookingAPIKey>(externalBookingAPIKey)
      )
    )

export const getExternalBookingAPIKeys = (params: TableParams) =>
  SBAPI.FetchPaginatedDispatched(
    fetchExternalBookingAPIKeys(params),
    [externalBookingAPIKeySchema],
    slice.actions
  )

export const createExternalBookingAPIKey =
  (values: ExternalBookingAPIKey) => async (dispatch: AppDispatch) => {
    try {
      const { data } = await createExternalBookingAPIKeyAPI(values)

      if (data.apiKey) {
        dispatch(setTempCreatedApiKey(data.apiKey))
      }

      return data
    } catch (error) {
      console.error(error)
      throw error
    }
  }

export const updateExternalBookingAPIKey = (
  externalBookingAPIKey: ExternalBookingAPIKey
) =>
  SBAPI.UpdateDispatched<ExternalBookingAPIKey>(
    updateExternalBookingAPIKeyAPI(externalBookingAPIKey),
    externalBookingAPIKey,
    slice.actions
  )

export const deleteExternalBookingAPIKey = (
  externalBookingAPIKey: ExternalBookingAPIKey
) =>
  SBAPI.DestroyDispatched<ExternalBookingAPIKey>(
    deleteExternalBookingAPIKeyAPI(externalBookingAPIKey),
    externalBookingAPIKey,
    slice.actions
  )
